import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import React, { forwardRef } from 'react'
import { Platform, StyleSheet } from 'react-native'
import { ReduceMotion } from 'react-native-reanimated'
import { tierName } from '@ppwr/convex/badges/catalog'
import { DS } from '../../design-system'
import { useMaxWidth } from '@/components/WebWrapper'
import { BadgeArt } from '../../../assets/badges/badgeAssets'
import { gridPalette, type BadgeDisplayState } from './badgeDisplay'

const isWeb = Platform.OS === 'web'
const ART_SIZE = 140

function formatEarnedDate(timestampMs: number): string {
  return new Date(timestampMs).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

interface BadgeDetailSheetProps {
  state: BadgeDisplayState | null
}

/**
 * Bottom-sheet detail for a tapped badge. Art mirrors the grid palette
 * (silhouette while unearned); earned badges show the tier palette plus
 * the date each tier was reached.
 */
export const BadgeDetailSheet = forwardRef<BottomSheetModal, BadgeDetailSheetProps>(({ state }, ref) => {
  const maxWidth = useMaxWidth()

  return (
    <BottomSheetModal
      ref={ref}
      name="badge-detail"
      overrideReduceMotion={ReduceMotion.System}
      index={0}
      enableDynamicSizing={true}
      backdropComponent={(props) => <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />}
      backgroundStyle={{ backgroundColor: DS.palette['Brand/Cream'] }}
      style={{
        marginHorizontal: isWeb ? 'auto' : undefined,
        maxWidth: maxWidth,
        width: '100%'
      }}
    >
      <BottomSheetView style={isWeb ? {} : { flex: 1 }}>
        {state ? (
          <DS.Frame gap="16" alignItems="center" flex={1} style={styles.container}>
            <BadgeArt badge={state.badge} palette={gridPalette(state)} size={ART_SIZE} />
            <DS.Frame gap="4" alignItems="center">
              <DS.Text variant="Heading 3/Medium" textAlign="center">
                {state.badge.name}
              </DS.Text>
              {state.tierName ? (
                <DS.Text variant="Caption/Bold" color="Brand/Dark Grey" uppercase>
                  {state.tierName}
                </DS.Text>
              ) : null}
              <DS.Text variant="Body Small/Regular" textAlign="center" color="Brand/Dark Grey">
                {state.description}
              </DS.Text>
            </DS.Frame>
            {state.isEarned ? (
              <DS.Frame gap="4" alignItems="center">
                {state.progressLabel ? (
                  <DS.Text variant="Body Small/Medium">{state.progressLabel}</DS.Text>
                ) : null}
                {state.tierEarnedAt.map((earnedAt, index) => (
                  <DS.Text key={`${state.badge.id}-tier-${index + 1}`} variant="Mini/Regular" color="Brand/Dark Grey">
                    {state.badge.tiers.length > 1
                      ? `${tierName(state.badge, index + 1)} — ${formatEarnedDate(earnedAt)}`
                      : `Earned ${formatEarnedDate(earnedAt)}`}
                  </DS.Text>
                ))}
              </DS.Frame>
            ) : (
              <DS.Frame gap="4" alignItems="center">
                {state.modalProgressLabel ? (
                  <DS.Text variant="Body Small/Medium">{state.modalProgressLabel}</DS.Text>
                ) : null}
                <DS.Text variant="Mini/Bold" color="Brand/Dark Grey" uppercase>
                  Locked
                </DS.Text>
              </DS.Frame>
            )}
          </DS.Frame>
        ) : null}
      </BottomSheetView>
    </BottomSheetModal>
  )
})

const styles = StyleSheet.create({
  container: {
    padding: DS.spacing[24],
    paddingTop: DS.spacing[8],
    paddingBottom: DS.spacing[32]
  }
})
